<template>
  <div>
    <v-card class="sf-card"
            flat>
      <v-card-text class="sf-card__body">
        <p>{{ $t('infoText') }}</p>
        <v-row align="center">
          <v-col cols="12"
                 sm="">
            <v-combobox hide-details
                        prepend-inner-icon="ti-search"
                        outlined
                        v-bind:items="personItems"
                        v-bind:label="$t('search')"
                        v-model="search"
                        v-on:input="input" />
          </v-col>
          <v-col cols="12"
                 sm="auto">
            <v-btn color="accent"
                   depressed
                   x-large
                   v-bind:block="$vuetify.breakpoint.xsOnly"
                   v-on:click="confirm(selected.participant)"
                   v-bind:disabled="selected.participant.wasAttending"
                   v-if="selected != null">
              <v-icon left>ti-check</v-icon>
              {{ $t('confirm') }}
            </v-btn>
            <v-btn color="primary"
                   depressed
                   x-large
                   v-bind:block="$vuetify.breakpoint.xsOnly"
                   v-else
                   v-on:click="input(search)">
              <v-icon left>ti-search</v-icon>
              {{ $t('search') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
      <template v-if="selected != null">
        <v-divider />
        <v-card-text class="sf-card__body">
          <v-alert text
                   type="warning"
                   v-if="!selected.participant.wasAttending">
            {{ selected.participant.isGuest ? $t('theGuestIsNotConfirmedYet', { name: selected.participant.name }) : $t('theCustomerIsNotConfirmedYet') }}
          </v-alert>
          <v-alert text
                   type="success"
                   v-else>
            {{ selected.participant.isGuest ? $t('theGuestsAttendanceWasSuccessfullyConfirmed', { name: selected.participant.name }) : $t('theCustomersAttendanceWasSuccessfullyConfirmed') }}
          </v-alert>
          <v-simple-table class="sf-kv">
            <tbody>
              <tr><td>{{ $t('customerNumber') }}</td><td>{{ selected.customer.id }}</td></tr>
              <tr><td>{{ $t('companyName') }}</td><td>{{ selected.customer.companyName }}</td></tr>
              <tr>
                <td>{{ $t('person') }}</td>
                <td>
                  {{ selected.participant.name }}
                  <v-chip class="ml-2"
                          label
                          x-small
                          v-if="selected.participant.isGuest">{{ $t('guest') }}</v-chip>
                </td>
              </tr>
              <tr v-if="selected.participant.isGuest"><td>{{ $t('host') }}</td><td>{{ selected.customer.givenName }} {{ selected.customer.familyName }}</td></tr>
              <tr><td>{{ $t('invitationCode') }}</td><td class="sf-mono">{{ selected.participant.invitationCode }}</td></tr>
            </tbody>
          </v-simple-table>

          <h3 class="sf-section-title mt-6">{{ $t('allPersons') }}</h3>
          <v-simple-table class="sf-persons">
            <tbody>
              <tr v-for="person in selectedParticipants"
                  v-bind:key="person.slot"
                  v-bind:class="{ 'sf-persons__selected': person.slot === selected.participant.slot }">
                <td>
                  {{ person.name }}
                  <v-chip class="ml-2"
                          label
                          x-small
                          v-if="person.isGuest">{{ $t('guest') }}</v-chip>
                </td>
                <td class="sf-mono">{{ person.invitationCode }}</td>
                <td class="text-right">
                  <v-chip color="success"
                          label
                          small
                          v-if="person.wasAttending">
                    <v-icon left
                            small>ti-check</v-icon>
                    {{ $t('onSite') }}
                  </v-chip>
                  <v-btn color="primary"
                         outlined
                         small
                         v-else
                         v-bind:disabled="person.invitationCode == null"
                         v-on:click="confirm(person)">
                    {{ $t('confirm') }}
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </template>
    </v-card>
  </div>
</template>

<script>
  import { findParticipantByInvitationCode, participants } from '@/utils/customer-guests'

  export default {
    computed: {
      // Eine Zeile je Person (Kunde und Gäste), nur angenommene Kunden
      personItems() {
        if (this.customers == null) {
          return []
        }

        const items = []

        for (const customer of this.customers.filter(c => c.state === 'accepted')) {
          for (const participant of participants(customer)) {
            items.push({
              value: `${customer.id}#${participant.slot}`,
              text: participant.isGuest
                ? `${customer.id} ${participant.name} (${customer.companyName}) · ${this.$t('guest')}`
                : `${customer.id} ${participant.name} (${customer.companyName})`
            })
          }
        }

        return items
      },
      // Immer aus dem aktuellen Store-Stand lesen, damit Bestätigungen sofort sichtbar sind
      selected() {
        if (this.selection == null || this.customers == null) {
          return null
        }

        const customer = this.customers.find(c => c.id === this.selection.customerId)

        if (customer == null) {
          return null
        }

        const participant = participants(customer).find(p => p.slot === this.selection.slot)

        return participant == null ? null : { customer, participant }
      },
      selectedParticipants() {
        return this.selected == null ? [] : participants(this.selected.customer)
      }
    },
    data: () => ({
      search: null,
      selection: null
    }),
    methods: {
      confirm(participant) {
        if (this.selected == null || participant == null) {
          return
        }

        this.$emit('confirm', { customer: this.selected.customer, slot: participant.slot })
      },
      input(value) {
        if (value != null && typeof value === 'object') {
          const [ customerId, slot ] = String(value.value).split('#')

          this.selection = { customerId, slot: Number(slot) }

          return
        }

        // Freitext: Kunden- oder Gastcode (auch vom Namensschild abgetippt)
        const match = findParticipantByInvitationCode(this.customers, typeof value === 'string' ? value : this.search)

        this.selection = match == null ? null : { customerId: match.customer.id, slot: match.participant.slot }
      }
    },
    name: 'AttendanceCard',
    props: {
      customers: Array
    }
  }
</script>

<style scoped>
  .sf-mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    letter-spacing: .04em;
  }

  .sf-persons__selected td {
    font-weight: 600;
  }
</style>

<i18n>
  {
    "de": {
      "allPersons": "Alle Personen dieses Kontos",
      "companyName": "Firmenname",
      "confirm": "Bestätigen",
      "customerNumber": "Kundennummer",
      "guest": "Gast",
      "host": "Ansprechpartner",
      "infoText": "Bitte geben Sie den Einladungscode der Person (Kunde oder Gast) oder deren Namen ein. Jede Person wird einzeln bestätigt.",
      "invitationCode": "Einladungscode",
      "onSite": "Vor Ort",
      "person": "Person",
      "search": "Suchen",
      "theCustomersAttendanceWasSuccessfullyConfirmed": "Die Anwesenheit des Kunden wurde erfolgreich bestätigt.",
      "theCustomerIsNotConfirmedYet": "Der Kunde ist noch nicht bestätigt.",
      "theGuestsAttendanceWasSuccessfullyConfirmed": "Die Anwesenheit von {name} (Gast) wurde erfolgreich bestätigt.",
      "theGuestIsNotConfirmedYet": "{name} (Gast) ist noch nicht bestätigt."
    },
    "en": {
      "allPersons": "All persons of this account",
      "companyName": "Company name",
      "confirm": "Confirm",
      "customerNumber": "Customer number",
      "guest": "Guest",
      "host": "Contact",
      "infoText": "Please enter the person's invitation code (customer or guest) or their name. Each person is confirmed individually.",
      "invitationCode": "Invitation code",
      "onSite": "On site",
      "person": "Person",
      "search": "Search",
      "theCustomersAttendanceWasSuccessfullyConfirmed": "The customer's attendance was successfully confirmed.",
      "theCustomerIsNotConfirmedYet": "The customer is not confirmed yet.",
      "theGuestsAttendanceWasSuccessfullyConfirmed": "The attendance of {name} (guest) was successfully confirmed.",
      "theGuestIsNotConfirmedYet": "{name} (guest) is not confirmed yet."
    }
  }
</i18n>
